import {showNotification} from 'platform/components';
import {match} from 'ts-pattern';

import {uniqBy} from 'ramda';
import {isArray} from 'ramda-adjunct';

import {getApiErrorMessages} from '@dms/api/utils';
import {
  useDeleteSupplierOrderMutation,
  usePutSupplierOrderHeaderActionMutation,
} from '@dms/api/warehouse';
import {i18n} from '@dms/i18n';
import {warehouseRoutes} from '@dms/routes';
import {
  DmsDataGrid,
  WAREHOUSE_GRID_CODES,
  catchUnhandledDataGridActions,
  handleApiError,
} from '@dms/shared';
import {WAREHOUSE_PDF_DEFINITIONS, useWarehouseGridPdfExport} from '@dms/warehouse/shared';
import {FullscreenDataGridWrapper} from '@dms/warehouse/shared-components';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

export function SupplierOrderList() {
  const onPdfExportCallback = useWarehouseGridPdfExport(
    i18n.t('page.warehouse.labels.supplierOrderList'),
    WAREHOUSE_PDF_DEFINITIONS.supplierOrders
  );
  const navigate = useNavigate();

  const [deleteSupplierOrder] = useDeleteSupplierOrderMutation();
  const [putSupplierOrderAction] = usePutSupplierOrderHeaderActionMutation();

  const actionCallback: ActionCallback = async ({
    actionKey,
    rowId,
    sourceSearchParams,
    refreshData,
  }) => {
    const id = isArray(rowId) ? rowId[0] : rowId;

    await match(actionKey)
      .with('redirectDetail', 'edit', () => {
        navigate(
          composePath(warehouseRoutes.supplierOrderDetailOverview, {
            queryParams: sourceSearchParams,
            params: {id},
            isSearchParamsPreserved: false,
          })
        );
      })
      .with('delete', async () => {
        const orderId = isArray(rowId) ? rowId : [rowId];

        await deleteSupplierOrder({body: {orderId}})
          .unwrap()
          .then(() => showNotification.success(i18n.t('general.notifications.successfullyDeleted')))
          .then(() => refreshData())
          .catch(handleApiError);
      })
      .with('cancel', async () => {
        const orderIds = isArray(rowId) ? rowId : [rowId];

        // CANCEL always returns normalizePrompt: false. Only SEND and LOCK ask for the bulk package verification.
        const results = await Promise.allSettled(
          orderIds.map((orderId) =>
            putSupplierOrderAction({orderId, body: {actionKey: 'CANCEL'}}).unwrap()
          )
        );

        const failures = results.filter(
          (result): result is PromiseRejectedResult => result.status === 'rejected'
        );
        if (failures.length === 0) {
          showNotification.success(i18n.t('general.notifications.successfullyCancelled'));
        } else {
          // Orders often fail for the same reason, so show each error message only once
          uniqBy((failure) => String(getApiErrorMessages(failure.reason)), failures).forEach(
            (failure) => handleApiError(failure.reason)
          );
        }

        // Refresh also after a failure, because the other orders can already be cancelled
        refreshData();
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  return (
    <FullscreenDataGridWrapper hideCardWrapper>
      <DmsDataGrid
        gridCode={WAREHOUSE_GRID_CODES.warehouseSupplierOrders}
        pdfColumns={WAREHOUSE_PDF_DEFINITIONS.supplierOrders.columns}
        onPdfExportCallback={onPdfExportCallback}
        actionCallback={actionCallback}
        data-testid={WAREHOUSE_GRID_CODES.warehouseSupplierOrders}
      />
    </FullscreenDataGridWrapper>
  );
}
